import React, { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { listUsers, createUser, updateUser, deleteUser, listClients, createClient, updateClient } from 'services/users.service'
import { getUser } from 'services/auth.service'
import { useI18n, apiError } from 'i18n'
import { ClientStatus, TerminateClientDialog, clientState, localDay } from 'components/ClientLifecycle'

const ROLES = ['client', 'system_admin']

const EMPTY_USER = { firstName: '', lastName: '', email: '', password: '', role: 'client', clientId: '', active: true }

const PLANS = ['trial', 'paid']

// Plan + shipment usage of a client, editable inline (e.g. move a trial to paid),
// and its lifecycle (terminate / reactivate).
const ClientRow = ({ client, onSaved, lifecycleBusy, onTerminate, onReactivate }) => {
  const { t } = useI18n()
  const [editing, setEditing] = useState(false)
  const [plan, setPlan] = useState(client.plan)
  const [limit, setLimit] = useState(client.shipmentLimit ?? '')
  const [busy, setBusy] = useState(false)

  const start = () => { setPlan(client.plan); setLimit(client.shipmentLimit ?? ''); setEditing(true) }
  const save = async () => {
    setBusy(true)
    try {
      await updateClient(client.clientId, { plan, shipmentLimit: limit === '' ? null : Number(limit) })
      toast.success(t('adminUsers.planUpdated'))
      setEditing(false)
      onSaved()
    } catch (error) {
      toast.error(apiError(error, t('adminUsers.planFailed')))
    } finally {
      setBusy(false)
    }
  }

  const usage = client.shipmentLimit === null || client.shipmentLimit === undefined
    ? t('adminUsers.usageUnlimited', { used: client.shipmentsUsed })
    : t('adminUsers.usageOf', { used: client.shipmentsUsed, limit: client.shipmentLimit })
  const reached = client.shipmentLimit !== null && client.shipmentLimit !== undefined && client.shipmentsUsed >= client.shipmentLimit
  const state = clientState(client)

  return (
    <tr className={`align-top ${state === 'purged' ? 'opacity-60' : ''}`}>
      <td className='px-4 py-3'>
        <p className='font-medium'>{client.name}</p>
        {client.signupSource === 'self' && <p className='text-xs text-slate-500'>{t('adminUsers.selfSignup')}</p>}
      </td>
      <td className='px-4 py-3 text-center text-slate-600'>{client.usersCount}</td>
      <td className='px-4 py-3'>
        {editing
          ? (
            <div className='flex flex-col gap-2'>
              <select value={plan} onChange={(e) => setPlan(e.target.value)} aria-label={t('adminUsers.plan')}
                className='rounded-lg border border-slate-300 px-2 py-1 text-sm'>
                {PLANS.map((p) => <option key={p} value={p}>{t(`plans.${p}`)}</option>)}
              </select>
              <input type='number' min='0' value={limit} onChange={(e) => setLimit(e.target.value)} placeholder={t('adminUsers.unlimited')}
                aria-label={t('adminUsers.shipmentLimit')} className='w-28 rounded-lg border border-slate-300 px-2 py-1 text-sm' />
              <div className='flex gap-2'>
                <button type='button' onClick={save} disabled={busy} className='text-xs font-semibold text-brand hover:underline disabled:opacity-50'>{t('common.save')}</button>
                <button type='button' onClick={() => setEditing(false)} className='text-xs font-semibold text-slate-500 hover:underline'>{t('common.cancel')}</button>
              </div>
            </div>
            )
          : (
            <div>
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${client.plan === 'trial' ? 'bg-brand/10 text-brand' : 'bg-status-ready/10 text-status-ready'}`}>
                {t(`plans.${client.plan}`)}
              </span>
              {state !== 'purged' && <p className={`mt-1 text-xs ${reached ? 'font-semibold text-status-review' : 'text-slate-500'}`}>{usage}</p>}
              {state !== 'purged' && <button type='button' onClick={start} className='mt-1 text-xs font-semibold text-brand hover:underline'>{t('common.edit')}</button>}
            </div>
            )}
      </td>
      <td className='px-4 py-3'>
        <ClientStatus client={client} busy={lifecycleBusy} onTerminate={onTerminate} onReactivate={onReactivate} />
      </td>
    </tr>
  )
}

const AdminUsers = () => {
  const { t, fmt } = useI18n()
  const meId = getUser()?.userId
  const [clients, setClients] = useState([])
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [clientName, setClientName] = useState('')
  const [form, setForm] = useState(EMPTY_USER)
  const [editingId, setEditingId] = useState(null)
  const [busy, setBusy] = useState(false)
  const [terminating, setTerminating] = useState(null)
  const [lifecycleBusy, setLifecycleBusy] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [c, u] = await Promise.all([listClients(), listUsers()])
      setClients(c.clients || [])
      setUsers(u.users || [])
    } catch (error) {
      toast.error(t('adminUsers.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const onCreateClient = async (e) => {
    e.preventDefault()
    if (!clientName.trim()) return
    try {
      await createClient(clientName.trim())
      setClientName('')
      toast.success(t('adminUsers.clientCreated'))
      load()
    } catch (error) {
      toast.error(apiError(error, t('adminUsers.clientFailed')))
    }
  }

  // Terminate: starts the export window; the API deletes the data on purgeAfter.
  const onConfirmTerminate = async (client) => {
    setLifecycleBusy(true)
    try {
      const { client: updated } = await updateClient(client.clientId, { active: false })
      toast.success(t('clientLifecycle.terminatedToast', { name: client.name, date: fmt.day(localDay(updated?.purgeAfter)) }))
      setTerminating(null)
      load()
    } catch (error) {
      toast.error(apiError(error, t('clientLifecycle.failed')))
    } finally {
      setLifecycleBusy(false)
    }
  }

  const onReactivate = async (client) => {
    setLifecycleBusy(true)
    try {
      await updateClient(client.clientId, { active: true })
      toast.success(t('clientLifecycle.reactivatedToast', { name: client.name }))
      load()
    } catch (error) {
      toast.error(apiError(error, t('clientLifecycle.failed')))
    } finally {
      setLifecycleBusy(false)
    }
  }

  const closeTerminate = useCallback(() => setTerminating(null), [])

  const setField = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setForm((f) => ({ ...f, [key]: value }))
  }

  const startEdit = (u) => {
    setEditingId(u.userId)
    setForm({ firstName: u.firstName, lastName: u.lastName, email: u.email, password: '', role: u.role, clientId: u.clientId || '', active: u.active })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const cancelEdit = () => {
    setEditingId(null)
    setForm(EMPTY_USER)
  }

  const onSubmitUser = async (e) => {
    e.preventDefault()
    setBusy(true)
    try {
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        role: form.role,
        active: form.active
      }
      if (form.role === 'client') payload.clientId = Number(form.clientId)
      if (form.password) payload.password = form.password

      if (editingId) {
        await updateUser(editingId, payload)
        toast.success(t('adminUsers.userUpdated'))
      } else {
        await createUser({ ...payload, email: form.email, password: form.password })
        toast.success(t('adminUsers.userCreated'))
      }
      cancelEdit()
      load()
    } catch (error) {
      toast.error(apiError(error, t('adminUsers.saveFailed')))
    } finally {
      setBusy(false)
    }
  }

  const onDelete = async (u) => {
    if (!window.confirm(t('adminUsers.confirmDelete', { email: u.email }))) return
    try {
      await deleteUser(u.userId)
      toast.success(t('adminUsers.userDeleted'))
      if (editingId === u.userId) cancelEdit()
      load()
    } catch (error) {
      toast.error(apiError(error, t('adminUsers.deleteFailed')))
    }
  }

  // Quick activate/deactivate: re-send the user's current values with active flipped.
  const onToggleActive = async (u) => {
    try {
      await updateUser(u.userId, {
        firstName: u.firstName,
        lastName: u.lastName,
        role: u.role,
        ...(u.role === 'client' ? { clientId: u.clientId } : {}),
        active: !u.active
      })
      toast.success(u.active ? t('adminUsers.userDeactivated') : t('adminUsers.userActivated'))
      load()
    } catch (error) {
      toast.error(apiError(error, t('adminUsers.updateFailed')))
    }
  }

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl font-bold'>{t('adminUsers.title')}</h1>
        <p className='text-sm text-slate-500'>{t('adminUsers.subtitle')}</p>
      </div>

      <div className='grid gap-6 lg:grid-cols-2'>
        {/* Clients */}
        <div className='space-y-4'>
          <form onSubmit={onCreateClient} className='flex items-end gap-2 rounded-xl border border-slate-200 bg-white p-5'>
            <div className='flex-1'>
              <label className='mb-1 block text-sm font-semibold text-slate-700'>{t('adminUsers.newClient')}</label>
              <input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder={t('adminUsers.clientName')}
                className='w-full rounded-lg border border-slate-300 px-3 py-2 text-sm' />
            </div>
            <button type='submit' className='rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-ink hover:bg-accent-600'>{t('common.add')}</button>
          </form>

          <div className='overflow-x-auto rounded-xl border border-slate-200 bg-white'>
            <table className='min-w-full divide-y divide-slate-200 text-sm'>
              <thead className='bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500'>
                <tr><th className='px-4 py-3'>{t('adminUsers.client')}</th><th className='px-4 py-3 text-center'>{t('adminUsers.users')}</th><th className='px-4 py-3'>{t('adminUsers.plan')}</th><th className='px-4 py-3'>{t('common.status')}</th></tr>
              </thead>
              <tbody className='divide-y divide-slate-100'>
                {loading && <tr><td colSpan={4} className='px-4 py-6 text-center text-slate-400'>{t('common.loading')}</td></tr>}
                {!loading && clients.length === 0 && <tr><td colSpan={4} className='px-4 py-6 text-center text-slate-400'>{t('adminUsers.noClients')}</td></tr>}
                {!loading && clients.map((c) => (
                  <ClientRow key={c.clientId} client={c} onSaved={load} lifecycleBusy={lifecycleBusy}
                    onTerminate={setTerminating} onReactivate={onReactivate} />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create / edit user */}
        <form onSubmit={onSubmitUser} className='space-y-3 rounded-xl border border-slate-200 bg-white p-5'>
          <div className='flex items-center justify-between'>
            <h2 className='text-sm font-semibold text-slate-700'>{editingId ? t('adminUsers.editUser') : t('adminUsers.createUser')}</h2>
            {editingId && <button type='button' onClick={cancelEdit} className='text-xs font-semibold text-slate-500 hover:underline'>{t('common.cancel')}</button>}
          </div>
          <div className='grid grid-cols-2 gap-3'>
            <input required placeholder={t('adminUsers.firstName')} value={form.firstName} onChange={setField('firstName')} className='rounded-lg border border-slate-300 px-3 py-2 text-sm' />
            <input required placeholder={t('adminUsers.lastName')} value={form.lastName} onChange={setField('lastName')} className='rounded-lg border border-slate-300 px-3 py-2 text-sm' />
          </div>
          <input required type='email' placeholder={t('adminUsers.email')} value={form.email} disabled={!!editingId} onChange={setField('email')}
            className='w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500' />
          <input type='password' placeholder={editingId ? t('adminUsers.newPassword') : t('adminUsers.password')} required={!editingId} minLength={8}
            value={form.password} onChange={setField('password')} className='w-full rounded-lg border border-slate-300 px-3 py-2 text-sm' />
          <div className='grid grid-cols-2 gap-3'>
            <select value={form.role} onChange={setField('role')} className='rounded-lg border border-slate-300 px-3 py-2 text-sm'>
              {ROLES.map((r) => <option key={r} value={r}>{t(`roles.${r}`)}</option>)}
            </select>
            {form.role === 'client' && (
              <select required value={form.clientId} onChange={setField('clientId')} className='rounded-lg border border-slate-300 px-3 py-2 text-sm'>
                <option value=''>{t('adminUsers.selectClient')}</option>
                {clients.filter((c) => clientState(c) !== 'purged').map((c) => <option key={c.clientId} value={c.clientId}>{c.name}</option>)}
              </select>
            )}
          </div>
          <label className='flex items-center gap-2 text-sm text-slate-600'>
            <input type='checkbox' checked={form.active} onChange={setField('active')} /> {t('adminUsers.active')}
          </label>
          <button type='submit' disabled={busy} className='w-full rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-ink hover:bg-accent-600 disabled:opacity-50'>
            {busy ? t('common.saving') : (editingId ? t('adminUsers.saveChanges') : t('adminUsers.createUser'))}
          </button>
        </form>
      </div>

      {/* Users table */}
      <div className='overflow-hidden rounded-xl border border-slate-200 bg-white'>
        <table className='min-w-full divide-y divide-slate-200 text-sm'>
          <thead className='bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500'>
            <tr>
              <th className='px-4 py-3'>{t('adminUsers.name')}</th>
              <th className='px-4 py-3'>{t('adminUsers.email')}</th>
              <th className='px-4 py-3'>{t('adminUsers.role')}</th>
              <th className='px-4 py-3'>{t('adminUsers.client')}</th>
              <th className='px-4 py-3 text-center'>{t('adminUsers.active')}</th>
              <th className='px-4 py-3 text-right'>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {loading && <tr><td colSpan={6} className='px-4 py-6 text-center text-slate-400'>{t('common.loading')}</td></tr>}
            {!loading && users.length === 0 && <tr><td colSpan={6} className='px-4 py-6 text-center text-slate-400'>{t('adminUsers.noUsers')}</td></tr>}
            {!loading && users.map((u) => (
              <tr key={u.userId} className={`hover:bg-slate-50 ${u.active ? '' : 'opacity-60'}`}>
                <td className='px-4 py-3 font-medium'>{u.firstName} {u.lastName}</td>
                <td className='px-4 py-3 text-slate-600'>{u.email}</td>
                <td className='px-4 py-3 text-slate-600'>{t(`roles.${u.role}`)}</td>
                <td className='px-4 py-3 text-slate-600'>{u.clientName || '—'}</td>
                <td className='px-4 py-3 text-center'>{u.active ? '✅' : '—'}</td>
                <td className='px-4 py-3'>
                  <div className='flex items-center justify-end gap-3'>
                    <button onClick={() => startEdit(u)} className='font-semibold text-brand hover:underline'>{t('common.edit')}</button>
                    {u.userId !== meId && (
                      <button onClick={() => onToggleActive(u)} className={`font-semibold hover:underline ${u.active ? 'text-status-review' : 'text-status-ready'}`}>
                        {u.active ? t('adminUsers.deactivate') : t('adminUsers.activate')}
                      </button>
                    )}
                    {u.userId !== meId && (
                      <button onClick={() => onDelete(u)} className='font-semibold text-status-incomplete hover:underline'>{t('common.delete')}</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <TerminateClientDialog client={terminating} busy={lifecycleBusy} onConfirm={onConfirmTerminate} onClose={closeTerminate} />
    </div>
  )
}

export default AdminUsers
